import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Share2, Download, Upload as UploadIcon, RotateCcw, LogOut,
  Mail, Check, AlertTriangle, User, Eye, EyeOff, Bell, BellOff, Wand2, Languages,
} from 'lucide-react';
import { TradeModal } from '../components/TradeModal';
import { useI18n } from '../lib/i18n';
import { useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { getPushState, enablePush, disablePush, type PushState } from '../lib/push';

type CollectionSticker = {
  id: string; number: number; code?: string | null;
  player_name?: string | null; team?: string | null;
  quantity: number;
};

export function Settings() {
  const { token, user, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState<null | 'export' | 'import' | 'reset' | 'push'>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [pushState, setPushState] = useState<PushState>({ kind: 'idle' });
  const [demoTradeOpen, setDemoTradeOpen] = useState(false);
  const { lang, setLang, t: tr } = useI18n();

  useEffect(() => {
    getPushState().then(setPushState).catch(() => setPushState({ kind: 'unsupported' }));
  }, []);

  async function togglePush() {
    if (!token) return;
    setBusy('push');
    try {
      if (pushState.kind === 'subscribed') {
        await disablePush(token);
        setPushState({ kind: 'idle' });
        flash('ok', tr('settings.toast.notifsOff'));
      } else {
        const next = await enablePush(token);
        setPushState(next);
        if (next.kind === 'subscribed') flash('ok', tr('settings.toast.notifsOn'));
        else if (next.kind === 'denied') flash('err', tr('settings.toast.notifsBlocked'));
      }
    } catch (e: any) {
      flash('err', e?.message ?? tr('settings.toast.notifsErr'));
    } finally {
      setBusy(null);
    }
  }

  async function sendTestPush() {
    if (!token) return;
    try {
      const r = await api.pushTest(token);
      if (r.sent > 0) flash('ok', `${tr('settings.toast.testOk')} ${r.sent} ${r.sent === 1 ? tr('settings.toast.deviceS') : tr('settings.toast.deviceP')}`);
      else flash('err', tr('settings.toast.testErr'));
    } catch {
      flash('err', tr('settings.toast.testCrash'));
    }
  }

  const { data: col } = useQuery({
    queryKey: ['collection'],
    queryFn: () => api.collection(token!),
    enabled: !!token,
  });
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(token!),
    enabled: !!token,
  });
  const privacyMut = useMutation({
    mutationFn: (privateMode: boolean) => api.updateMe({ privateMode }, token!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      flash('ok', tr('settings.toast.privacyOk'));
    },
    onError: () => flash('err', tr('settings.toast.privacyErr')),
  });

  function flash(kind: 'ok' | 'err', msg: string) {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2400);
  }

  async function onShare() {
    const pct = col?.completionPct ?? 0;
    const name = user?.displayName?.split(' ')[0] ?? 'alguien';
    const url = `${location.origin}/?from=${encodeURIComponent(name)}&pct=${pct}&lang=${lang}`;
    const text = `${tr('share.text')} ${pct}% ${tr('share.complete')} ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Pannini 2026', text, url });
      } else {
        await navigator.clipboard.writeText(text);
      }
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    } catch {
      // user cancelled — no-op
    }
  }

  function onExport() {
    if (!col) return;
    setBusy('export');
    const owned = (col.stickers as CollectionSticker[])
      .filter((s) => s.quantity > 0)
      .map((s) => ({ id: s.id, number: s.number, code: s.code, quantity: s.quantity }));
    const payload = {
      app: 'pannini-2026',
      album: 'mundial-2026',
      exportedAt: new Date().toISOString(),
      user: { displayName: user?.displayName, email: user?.email },
      completionPct: col.completionPct,
      stickers: owned,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pannini-2026-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setBusy(null);
    flash('ok', tr('settings.toast.backup'));
  }

  async function onImportFile(file: File) {
    setBusy('import');
    try {
      const text = await file.text();
      const data = JSON.parse(text) as {
        stickers: { id: string; quantity: number }[];
      };
      if (!Array.isArray(data?.stickers)) throw new Error('formato_invalido');
      const updates = data.stickers
        .filter((s) => typeof s.id === 'string' && Number.isFinite(s.quantity))
        .map((s) => ({ stickerId: s.id, quantity: Math.max(0, Math.floor(s.quantity)) }));
      if (updates.length === 0) throw new Error('vacio');
      await api.bulkUpdate(updates, token!);
      await qc.invalidateQueries({ queryKey: ['collection'] });
      flash('ok', `${tr('settings.toast.restored')} ${updates.length}`);
    } catch (e: any) {
      flash('err', e.message === 'formato_invalido' ? tr('settings.toast.invalid') : tr('settings.toast.notRestored'));
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function onReset() {
    if (!col) return;
    setBusy('reset');
    try {
      const owned = (col.stickers as CollectionSticker[]).filter((s) => s.quantity > 0);
      if (owned.length === 0) {
        flash('ok', tr('settings.toast.empty'));
        setBusy(null);
        setConfirmReset(false);
        return;
      }
      const updates = owned.map((s) => ({ stickerId: s.id, quantity: 0 }));
      await api.bulkUpdate(updates, token!);
      await qc.invalidateQueries({ queryKey: ['collection'] });
      flash('ok', tr('settings.toast.reset'));
    } catch {
      flash('err', tr('settings.toast.notReset'));
    } finally {
      setBusy(null);
      setConfirmReset(false);
    }
  }

  function onLogout() {
    logout();
    nav('/', { replace: true });
  }

  const initial = user?.displayName?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="space-y-6">
      <header className="pt-2">
        <p className="text-haze text-sm">{tr('settings.eyebrow')}</p>
        <h1 className="font-display font-extrabold text-3xl text-balance">
          {tr('settings.title1')} <span className="text-gradient">{tr('settings.titleAccent')}</span>
        </h1>
      </header>

      {/* Profile */}
      <section className="glass-strong rounded-3xl p-5 flex items-center gap-4">
        <div className="grid place-items-center h-14 w-14 rounded-2xl bg-flame/20 text-flame font-display font-extrabold text-xl border border-flame/40">
          {initial}
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-lg truncate">{user?.displayName}</div>
          <div className="text-xs text-haze flex items-center gap-1 truncate">
            <Mail size={12} /> {user?.email}
          </div>
        </div>
      </section>

      {/* Language */}
      <section>
        <SectionTitle>{tr('settings.lang.title')}</SectionTitle>
        <div className="glass rounded-2xl p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-cyan/15 text-cyan border border-cyan/30">
            <Languages size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">{tr('settings.lang.title')}</div>
            <div className="text-2xs text-haze">
              {lang === 'es' ? tr('settings.lang.desc.es') : tr('settings.lang.desc.en')}
            </div>
          </div>
          <div className="flex rounded-full bg-white/5 border border-white/10 p-0.5 text-2xs font-bold shrink-0">
            <button
              onClick={() => setLang('es')}
              className={`px-3 py-1 rounded-full transition ${lang === 'es' ? 'bg-flame text-white shadow-glow-flame' : 'text-haze hover:text-foam'}`}
            >
              ES
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1 rounded-full transition ${lang === 'en' ? 'bg-flame text-white shadow-glow-flame' : 'text-haze hover:text-foam'}`}
            >
              EN
            </button>
          </div>
        </div>
      </section>

      {/* Trade demo (sólo para probar el UI sin necesidad de otro user) */}
      <section>
        <SectionTitle>{tr('settings.demo.title')}</SectionTitle>
        <button
          onClick={() => setDemoTradeOpen(true)}
          disabled={!col || (col?.stickers?.length ?? 0) === 0}
          className="w-full glass rounded-2xl p-4 flex items-center gap-3 hover:bg-graphite/40 transition disabled:opacity-50"
        >
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-flame/15 text-flame border border-flame/35">
            <Wand2 size={18} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold">{tr('settings.demo.tradeModal')}</div>
            <div className="text-2xs text-haze">{tr('settings.demo.tradeModalDesc')}</div>
          </div>
        </button>
      </section>

      {/* Notifications */}
      <section>
        <SectionTitle>{tr('settings.notif.title')}</SectionTitle>
        <div className="space-y-2">
          <div className="glass rounded-2xl p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl grid place-items-center border ${
              pushState.kind === 'subscribed'
                ? 'bg-cyan/15 text-cyan border-cyan/35'
                : 'bg-haze/10 text-haze border-haze/20'
            }`}>
              {pushState.kind === 'subscribed' ? <Bell size={18} /> : <BellOff size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{tr('settings.notif.push')}</div>
              <div className="text-2xs text-haze">
                {pushState.kind === 'unsupported' && tr('settings.notif.unsupported')}
                {pushState.kind === 'denied' && tr('settings.notif.denied')}
                {pushState.kind === 'idle' && tr('settings.notif.idle')}
                {pushState.kind === 'subscribed' && tr('settings.notif.subscribed')}
              </div>
            </div>
            {pushState.kind !== 'unsupported' && pushState.kind !== 'denied' && (
              <button
                onClick={togglePush}
                disabled={busy === 'push'}
                aria-label={tr('settings.notif.push')}
                className={`relative h-7 w-12 rounded-full transition disabled:opacity-50 ${
                  pushState.kind === 'subscribed' ? 'bg-cyan/70' : 'bg-haze/30'
                }`}
              >
                <span
                  className={`absolute top-1 h-5 w-5 rounded-full bg-foam shadow transition-all ${
                    pushState.kind === 'subscribed' ? 'left-6' : 'left-1'
                  }`}
                />
              </button>
            )}
          </div>
          {pushState.kind === 'subscribed' && (
            <button
              onClick={sendTestPush}
              className="w-full glass rounded-2xl p-3 text-sm font-semibold text-cyan hover:bg-graphite/40 transition flex items-center justify-center gap-2"
            >
              <Bell size={14} /> {tr('settings.notif.sendTest')}
            </button>
          )}
        </div>
      </section>

      {/* Privacy */}
      <section>
        <SectionTitle>{tr('settings.privacy.title')}</SectionTitle>
        <div className="glass rounded-2xl p-4 flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl grid place-items-center border ${
            me?.privateMode
              ? 'bg-flame/15 text-flame border-flame/30'
              : 'bg-lime/15 text-lime border-lime/30'
          }`}>
            {me?.privateMode ? <EyeOff size={18} /> : <Eye size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">{tr('settings.privacy.private')}</div>
            <div className="text-2xs text-haze">
              {me?.privateMode
                ? tr('settings.privacy.privateDesc')
                : tr('settings.privacy.publicDesc')}
            </div>
          </div>
          <button
            onClick={() => privacyMut.mutate(!me?.privateMode)}
            disabled={!me || privacyMut.isPending}
            aria-label={tr('settings.privacy.private')}
            className={`relative h-7 w-12 rounded-full transition disabled:opacity-50 ${
              me?.privateMode ? 'bg-flame/70' : 'bg-lime/70'
            }`}
          >
            <span
              className={`absolute top-1 h-5 w-5 rounded-full bg-foam shadow transition-all ${
                me?.privateMode ? 'left-6' : 'left-1'
              }`}
            />
          </button>
        </div>
      </section>

      {/* Share */}
      <section>
        <SectionTitle>{tr('settings.share.title')}</SectionTitle>
        <button
          onClick={onShare}
          className="w-full glass rounded-2xl p-4 flex items-center gap-3 hover:bg-graphite/40 transition"
        >
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-cyan/15 text-cyan border border-cyan/30">
            {shared ? <Check size={18} /> : <Share2 size={18} />}
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold">{shared ? tr('settings.share.copied') : tr('settings.share.cta')}</div>
            <div className="text-2xs text-haze">{tr('settings.share.desc')}</div>
          </div>
        </button>
      </section>

      {/* Backup */}
      <section>
        <SectionTitle>{tr('settings.backup.title')}</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onExport}
            disabled={!col || busy === 'export'}
            className="glass rounded-2xl p-4 flex flex-col items-start gap-2 hover:bg-graphite/40 transition disabled:opacity-50"
          >
            <div className="h-9 w-9 rounded-xl grid place-items-center bg-lime/15 text-lime border border-lime/30">
              <Download size={16} />
            </div>
            <div className="font-semibold text-sm">{tr('settings.backup.export')}</div>
            <div className="text-2xs text-haze text-left">{tr('settings.backup.exportDesc')}</div>
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy === 'import'}
            className="glass rounded-2xl p-4 flex flex-col items-start gap-2 hover:bg-graphite/40 transition disabled:opacity-50"
          >
            <div className="h-9 w-9 rounded-xl grid place-items-center bg-royal/15 text-[#6AA0FF] border border-royal/30">
              <UploadIcon size={16} />
            </div>
            <div className="font-semibold text-sm">{tr('settings.backup.import')}</div>
            <div className="text-2xs text-haze text-left">{tr('settings.backup.importDesc')}</div>
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onImportFile(f);
          }}
        />
      </section>

      {/* Danger zone */}
      <section>
        <SectionTitle>{tr('settings.danger.title')}</SectionTitle>
        <div className="rounded-2xl border border-flame/30 bg-flame/5 p-4 space-y-3">
          {!confirmReset ? (
            <button
              onClick={() => setConfirmReset(true)}
              className="w-full flex items-center gap-3 text-left"
            >
              <div className="h-9 w-9 rounded-xl grid place-items-center bg-flame/15 text-flame border border-flame/30">
                <RotateCcw size={16} />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm">{tr('settings.reset.title')}</div>
                <div className="text-2xs text-haze">{tr('settings.reset.desc')}</div>
              </div>
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-flame text-sm">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <p>{tr('settings.reset.confirm')}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setConfirmReset(false)}
                  className="btn-ghost text-sm"
                  disabled={busy === 'reset'}
                >
                  {tr('common.cancel')}
                </button>
                <button
                  onClick={onReset}
                  className="btn-primary text-sm"
                  disabled={busy === 'reset'}
                >
                  {busy === 'reset' ? tr('settings.reset.doing') : tr('settings.reset.yes')}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Logout */}
      <section>
        <button
          onClick={onLogout}
          className="w-full glass rounded-2xl p-4 flex items-center gap-3 hover:bg-graphite/40 transition"
        >
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-haze/10 text-haze border border-haze/20">
            <LogOut size={18} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold">{tr('settings.logout')}</div>
            <div className="text-2xs text-haze">{user?.email}</div>
          </div>
        </button>
      </section>

      <p className="text-center text-2xs text-haze pt-2 pb-4 flex items-center justify-center gap-1">
        <User size={10} /> Pannini 2026 · v1
      </p>

      {/* toast */}
      {toast && (
        <div
          className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full text-sm font-semibold shadow-lg ${
            toast.kind === 'ok'
              ? 'bg-lime text-[#06112E]'
              : 'bg-flame text-white'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Trade demo modal */}
      {demoTradeOpen && col && (() => {
        const stickers = (col.stickers ?? []) as any[];
        const giveNums = stickers.slice(0, 5).map((s) => s.number);
        const receiveNums = stickers.slice(5, 10).map((s) => s.number);
        return (
          <TradeModal
            isOpen
            onClose={() => setDemoTradeOpen(false)}
            receiver={{ id: 'demo', displayName: 'Juan Demo', city: 'Bogotá', avatarUrl: null }}
            myStickers={stickers}
            iCanGiveNumbers={giveNums}
            iCanReceiveNumbers={receiveNums}
            score={0.85}
            onSent={() => {
              flash('ok', tr('settings.toast.tradeDemoSent'));
              setDemoTradeOpen(false);
            }}
            demoMode
          />
        );
      })()}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xs uppercase tracking-wider text-haze font-semibold mb-2 px-1">
      {children}
    </h2>
  );
}
